import React from 'react';
import { Store } from '../../services/database/vision';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { TrendingUp, PieChart, BarChart4 } from 'lucide-react';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface FinancialAnalyticsProps {
  stores: Store[];
}

export const FinancialAnalytics: React.FC<FinancialAnalyticsProps> = ({ stores }) => {
  // Função para calcular dados por mês
  const calculateMonthlyData = () => {
    const monthlyData = Array(12).fill(0).map(() => ({ revenue: 0, costs: 0, profit: 0 }));
    
    stores.forEach(store => {
      const date = new Date(store.created_at);
      const month = date.getMonth();
      
      const costs = store.costs || { sm: 0, banners: 0, motion: 0 };
      const totalCost = Object.values(costs).reduce((a, b) => a + b, 0);
      
      monthlyData[month].revenue += store.price || 0;
      monthlyData[month].costs += totalCost;
      monthlyData[month].profit += (store.price || 0) - totalCost;
    });
    
    return monthlyData;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Dados para o gráfico de linha (Receita x Tempo)
  const monthlyData = calculateMonthlyData();
  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  
  const revenueData = {
    labels: months,
    datasets: [
      {
        label: 'Receita',
        data: monthlyData.map(d => d.revenue),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Lucro',
        data: monthlyData.map(d => d.profit),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: 'rgba(255, 255, 255, 0.8)',
          font: {
            size: 12
          },
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += formatCurrency(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(255, 255, 255, 0.05)'
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.6)',
          callback: (value: number) => formatCurrency(value)
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.6)'
        }
      }
    }
  };

  // Dados para o gráfico de distribuição por plano
  const planData = {
    labels: ['Start', 'Premium', 'Branding'],
    datasets: [{
      data: [
        stores.filter(s => s.plan === 'start').reduce((sum, s) => sum + (s.price || 0), 0),
        stores.filter(s => s.plan === 'premium').reduce((sum, s) => sum + (s.price || 0), 0),
        stores.filter(s => s.plan === 'branding').reduce((sum, s) => sum + (s.price || 0), 0)
      ],
      backgroundColor: [
        'rgba(34, 197, 94, 0.8)',  // Verde para Start
        'rgba(59, 130, 246, 0.8)', // Azul para Premium
        'rgba(245, 158, 11, 0.8)'  // Amarelo para Branding
      ],
      borderColor: [
        'rgb(34, 197, 94)',
        'rgb(59, 130, 246)',
        'rgb(245, 158, 11)'
      ],
      borderWidth: 1
    }]
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          color: 'rgba(255, 255, 255, 0.8)',
          font: {
            size: 12
          },
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleColor: '#fff',
        bodyColor: '#fff',
        callbacks: {
          label: function(context: any) {
            const value = context.raw;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${context.label}: ${formatCurrency(value)} (${percentage}%)`;
          }
        }
      }
    }
  };

  // Dados para o gráfico de custos
  const costData = {
    labels: months,
    datasets: [
      {
        label: 'Social Media',
        data: monthlyData.map((_, i) => 
          stores
            .filter(s => new Date(s.created_at).getMonth() === i)
            .reduce((sum, s) => sum + ((s.costs?.sm || 0)), 0)
        ),
        backgroundColor: 'rgba(34, 197, 94, 0.8)'
      },
      {
        label: 'Banners',
        data: monthlyData.map((_, i) => 
          stores
            .filter(s => new Date(s.created_at).getMonth() === i)
            .reduce((sum, s) => sum + ((s.costs?.banners || 0)), 0)
        ),
        backgroundColor: 'rgba(59, 130, 246, 0.8)'
      },
      {
        label: 'Motion',
        data: monthlyData.map((_, i) => 
          stores
            .filter(s => new Date(s.created_at).getMonth() === i)
            .reduce((sum, s) => sum + ((s.costs?.motion || 0)), 0)
        ),
        backgroundColor: 'rgba(245, 158, 11, 0.8)'
      }
    ]
  };

  const barOptions = {
    ...chartOptions,
    plugins: {
      ...chartOptions.plugins,
      legend: {
        ...chartOptions.plugins.legend,
        position: 'top' as const
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Gráfico de Receita x Tempo */}
      <div className="bg-[#111]/90 rounded-[20px] border border-white/[0.08] p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/20">
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <h3 className="text-lg font-medium">Evolução Financeira</h3>
              <p className="text-sm text-white/60">Receita e lucro ao longo do tempo</p>
            </div>
          </div>
        </div>
        <div className="h-[300px]">
          <Line data={revenueData} options={chartOptions} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Gráfico de Distribuição por Plano */}
        <div className="bg-[#111]/90 rounded-[20px] border border-white/[0.08] p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <PieChart className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h3 className="text-lg font-medium">Distribuição por Plano</h3>
              <p className="text-sm text-white/60">Receita dividida por plano</p>
            </div>
          </div>
          <div className="h-[300px] flex items-center">
            <Doughnut data={planData} options={pieOptions} />
          </div>
        </div>

        {/* Gráfico de Custos */}
        <div className="bg-[#111]/90 rounded-[20px] border border-white/[0.08] p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <BarChart4 className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h3 className="text-lg font-medium">Composição dos Custos</h3>
              <p className="text-sm text-white/60">Custos por categoria ao longo do tempo</p>
            </div>
          </div>
          <div className="h-[300px]">
            <Bar data={costData} options={barOptions} />
          </div>
        </div>
      </div>
    </div>
  );
};
